import React from "react";
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Fade,
  Divider
} from "@mui/material";
import {
  Schedule as ScheduleIcon,
  Chat as ChatIcon,
  Payment as PaymentIcon,
  PhotoCamera as PhotoIcon,
  Star as StarIcon,
  Checklist as ChecklistIcon,
  ArrowForward as ArrowIcon
} from "@mui/icons-material";
import { Link } from "react-router-dom";

const FeaturesMenu = ({ open, anchorEl, onClose }) => {
  const theme = useTheme();

  const features = [
    {
      icon: <ScheduleIcon />,
      title: "Automatic Scheduling",
      description: "Sync with Airbnb calendar",
      path: "/features/automatic-scheduling"
    },
    {
      icon: <ChatIcon />,
      title: "In-App Messaging",
      description: "Direct communication",
      path: "/features/in-app-messaging"
    },
    {
      icon: <PaymentIcon />,
      title: "Auto Payment",
      description: "Secure automated payments",
      path: "/features/auto-payment"
    },
    {
      icon: <PhotoIcon />,
      title: "Before & After Photos",
      description: "Visual proof of quality",
      path: "/features/before-after-photos"
    },
    {
      icon: <StarIcon />,
      title: "Cleanliness Score",
      description: "AI-powered quality control",
      path: "/features/cleanliness-score"
    },
    {
      icon: <ChecklistIcon />,
      title: "Task Checklist",
      description: "Customizable checklists",
      path: "/features/task-checklist"
    }
  ];

  if (!open) return null;

  const anchorRect = anchorEl?.getBoundingClientRect();

  return (
    <Fade in={open}>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          top: anchorRect ? anchorRect.bottom + 8 : 64,
          left: anchorRect ? anchorRect.left : 0,
          minWidth: 300,
          maxWidth: 350,
          background: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          zIndex: 1300,
          overflow: "hidden"
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
          <Typography variant="h6" fontWeight="600" color={theme.palette.text.primary}>
            Features
          </Typography>
          <Typography variant="body2" color={theme.palette.text.secondary}>
            Everything you need to manage cleanings
          </Typography>
        </Box>

        {/* Features List */}
        <List sx={{ p: 1 }}>
          {features.map((feature, index) => (
            <ListItem
              key={index}
              component={Link}
              to={feature.path}
              onClick={onClose}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                color: 'inherit',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.08),
                  transform: 'translateX(4px)'
                },
                '&:last-child': {
                  mb: 0
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: theme.palette.primary.main }}>
                {feature.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body1" fontWeight="500">
                    {feature.title}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    {feature.description}
                  </Typography>
                }
              />
              <ArrowIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            </ListItem>
          ))}
        </List>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, textAlign: 'center' }}>
          <Typography variant="body2" color={theme.palette.text.secondary}>
            Need help choosing?{" "}
            <Typography
              component="span"
              variant="body2"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={() => {
                onClose();
                window.location.href = '/contact';
              }}
            >
              Contact us
            </Typography>
          </Typography>
        </Box>
      </Paper>
    </Fade>
  );
};

export default FeaturesMenu;